import * as React from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Grid, TextField } from '@mui/material';
import { IconCheck } from '@tabler/icons';

import { useTags } from 'hooks/useTags';
import { TAG_DEFAULT_COLOR, TAG_PRESET_COLORS } from 'constants/tagColors';
import SelectAllMulti from 'ui-component/SelectAllMulti';
import ConfirmationDialog from 'ui-component/ConfirmationDialog';
import InputLabel from 'ui-component/extended/Form/InputLabel';

function TagColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
	return (
		<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
			{TAG_PRESET_COLORS.map((color) => (
				<Box
					key={color}
					onClick={() => onChange(color)}
					sx={{
						width: 32,
						height: 32,
						borderRadius: '50%',
						backgroundColor: color,
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						border: value === color ? '2px solid' : '2px solid transparent',
						borderColor: value === color ? 'text.primary' : 'transparent',
						opacity: value === color ? 0.85 : 1,
					}}
				>
					{value === color && <IconCheck color="#000" size={18} />}
				</Box>
			))}
		</Stack>
	);
}

export default function TagsEditor(props: any) {
	const dispatch = useDispatch();
  const { setChecked, initialValues } = props;
	
	const [selectedTags, setSelectedTags] = React.useState<any[]>(initialValues);
	const [tagOptions, setTagOptions] = React.useState<any[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const [showNewTagDialog, setShowNewTagDialog] = React.useState(false);
  const [showEditTagDialog, setShowEditTagDialog] = React.useState(false);
	
	const tagsState = useTags();
	const activeTagID = React.useRef<number | null>(null);
		
	const addNewTag = function() {
		formik.setValues({
			name: '',
			color: TAG_DEFAULT_COLOR,
			submit: null,
		});
		setShowNewTagDialog(true);
	}

	const editTag = function(tagID: number) {
		activeTagID.current = tagID;
		const tag = Object.values(tagsState.tags || {}).find((t: any) => t.id == tagID) as any;
		editFormik.setValues({
			name: tag?.name || '',
			color: tag.color,
			submit: null,
		});
		setShowEditTagDialog(true);
	}
	
	const deleteTag = function(tagID:number) {
		activeTagID.current = tagID;
		setShowDeleteConfirmation(true);
	}
	
	const confirmDeleteTag = async () => {
		await tagsState.deleteTag(activeTagID.current);
		
		dispatch(
				openSnackbar({
						open: true,
						message: 'Tag has been deleted.',
						variant: 'alert',
						alert: {
								color: 'success'
						},
						close: false
				})
		);
		
    setShowDeleteConfirmation(false);
		setSelectedTags([...selectedTags.filter(function(tag) { return parseInt(tag.id) != activeTagID.current; })]);
  };
	
	React.useEffect(()=>{
			if(tagsState?.ready) {
				let tags : any = [];
				Object.values(tagsState.tags)
						.sort((t1: any, t2: any) => (t1.name < t2.name ? -1 : 1))
						.map((tag: any) => tags.push({
							id: tag.id.toString(),
							label: tag.name,
							color: tag.color,
						}));
				setTagOptions(tags);

				// Keep selected chips in sync with latest name/color from the server
				setSelectedTags((prev) =>
					prev.map((selected) => {
						const match = tags.find((option: any) => option.id === selected.id);
						return match ? { ...selected, label: match.label, color: match.color } : selected;
					})
				);
			}
	}, [tagsState?.ready, tagsState.tags]);
	
	
	// yup validation-schema
	const validationSchema = yup.object({
	    name: yup.string().required('Name is required'),
			color: yup.string().oneOf([...TAG_PRESET_COLORS]).required('Color is required'),
	});
	
	const formik = useFormik({
			initialValues: {
					name: '',
					color: TAG_DEFAULT_COLOR,
					submit: null,
			},
			validationSchema,
			onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
					try {
						  let newTagID = await tagsState.createTag(values);
							setStatus({ success: true });
							setSubmitting(false);
							dispatch(
									openSnackbar({
											open: true,
											message: 'New tag has been created.',
											variant: 'alert',
											alert: {
													color: 'success'
											},
											close: false
									})
							);
							setShowNewTagDialog(false);
							setSelectedTags([...selectedTags, {id: String(newTagID), label: values.name, color: values.color}]);
					} catch (errors: any) {
							setStatus({ success: false });
							let errorMessage = '';
							for (const errorKey in errors) {
									errorMessage += errors[errorKey].join('\n') + '\n';
							}
							setErrors({ submit: errorMessage });
							setSubmitting(false);
					}
			}
	});

	const editFormik = useFormik({
			initialValues: {
					name: '',
					color: TAG_DEFAULT_COLOR,
					submit: null,
			},
			validationSchema,
			onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
					try {
							await tagsState.updateTag(activeTagID.current as number, values);
							setStatus({ success: true });
							setSubmitting(false);
							dispatch(
									openSnackbar({
											open: true,
											message: 'Tag has been updated.',
											variant: 'alert',
											alert: {
													color: 'success'
											},
											close: false
									})
							);
							setShowEditTagDialog(false);
							setSelectedTags(
								selectedTags.map((tag) =>
									parseInt(tag.id) === activeTagID.current
										? { ...tag, label: values.name, color: values.color }
										: tag
								)
							);
					} catch (errors: any) {
							setStatus({ success: false });
							let errorMessage = '';
							for (const errorKey in errors) {
									errorMessage += errors[errorKey].join('\n') + '\n';
							}
							setErrors({ submit: errorMessage });
							setSubmitting(false);
					}
			}
	});

  return (
		<>
		<SelectAllMulti
			label="Tags"
			options={tagOptions}
			setChecked={setChecked}                                           
			editable={true}
			addNewClicked={addNewTag}
			editClicked={editTag}
			deleteClicked={deleteTag}
			initialValues={initialValues}
			controlledValue={selectedTags}
			setControlledValue={setSelectedTags}
		/>
		<ConfirmationDialog
				open={showDeleteConfirmation}
				onCancel={() => setShowDeleteConfirmation(false)}
				onConfirm={confirmDeleteTag}
				title="Delete Tag?"
				message="Confirm you want to delete this tag? It will also be removed/unapplied from all applied programs and users"
		/>
		<Dialog 
			open={showNewTagDialog}
			maxWidth="sm"
			fullWidth={true}
		>
		<DialogTitle id="form-dialog-title">Add New Tag</DialogTitle>
				<DialogContent>
					<form onSubmit={formik.handleSubmit}>
								<Grid item xs={12} >
										<Stack spacing={2}>
												<Stack>
														<InputLabel required>Name</InputLabel>
														<TextField
																fullWidth
																id="name"
																name="name"
																value={formik.values.name}
																onChange={formik.handleChange}
																onBlur={formik.handleBlur}
																error={formik.touched.name && Boolean(formik.errors.name)}
																helperText={formik.touched.name && formik.errors.name}
														/>
												</Stack>
												<Stack>
														<InputLabel required>Color</InputLabel>
														<TagColorPicker
																value={formik.values.color}
																onChange={(color) => formik.setFieldValue('color', color)}
														/>
												</Stack>
										</Stack>
								</Grid>
						</form>
					</DialogContent>
					<DialogActions sx={{ pr: 2.5 }}>
							<Button variant="contained" autoFocus onClick={()=>setShowNewTagDialog(false)} >
									Cancel
							</Button>
							<Button variant="contained" autoFocus onClick={()=>formik.handleSubmit()} >
									Save
							</Button>
					</DialogActions>
		</Dialog>
		<Dialog
			open={showEditTagDialog}
			maxWidth="sm"
			fullWidth={true}
		>
		<DialogTitle id="edit-tag-dialog-title">Edit Tag</DialogTitle>
				<DialogContent>
					<form onSubmit={editFormik.handleSubmit}>
								<Grid item xs={12} >
										<Stack spacing={2}>
												<Stack>
														<InputLabel required>Name</InputLabel>
														<TextField
																fullWidth
																id="edit-name"
																name="name"
																value={editFormik.values.name}
																onChange={editFormik.handleChange}
																onBlur={editFormik.handleBlur}
																error={editFormik.touched.name && Boolean(editFormik.errors.name)}
																helperText={editFormik.touched.name && editFormik.errors.name}
														/>
												</Stack>
												<Stack>
														<InputLabel required>Color</InputLabel>
														<TagColorPicker
																value={editFormik.values.color}
																onChange={(color) => editFormik.setFieldValue('color', color)}
														/>
												</Stack>
										</Stack>
								</Grid>
						</form>
					</DialogContent>
					<DialogActions sx={{ pr: 2.5 }}>
							<Button variant="contained" autoFocus onClick={()=>setShowEditTagDialog(false)} >
									Cancel
							</Button>
							<Button variant="contained" autoFocus onClick={()=>editFormik.handleSubmit()} >
									Save
							</Button>
					</DialogActions>
		</Dialog>
		</>
  );
}


